import { useState, useEffect, useRef, useMemo } from 'react';
import useFetchAllianceChatMessages from '../api/useFetchAllianceChatMessages';
import useSendAllianceChatMessage from '../api/useSendAllianceChatMessage';
import useReadAllianceChatMessages from '../api/useReadAllianceChatMessages';
import type AgentApiAllianceChatMessagesRecord from '@/types/serializers/AgentApiAllianceChatMessagesRecord';
import { MdSend, MdAttachFile, MdDescription, MdCancel } from 'react-icons/md';
import ChatMessageAttachments from '@/components/ChatMessageAttachments';
import { Button } from '@/components/Button';
import { ATTACHMENT_ACCEPT, validateAttachments } from '@/utils/chatAttachments';

// 末尾の記号（句読点・閉じ括弧）はURLから除外する
const URL_REGEX = /(https?:\/\/[^\s]+)/g;
const TRAILING_PUNCT_REGEX = /[).,;:!?！？。、，．）]+$/;

// チャット入力欄の自動リサイズ設定（1行=24px、最大6行まで拡張しそれ以降はスクロール）
const CHAT_INPUT_LINE_HEIGHT = 24;
const CHAT_INPUT_MAX_ROWS = 6;

interface AllianceChatPanelProps {
  applicationId: string;
  agentName: string;
}

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('ja-JP', { hour: '2-digit', minute: '2-digit' }).format(date);
};

const formatDateLabel = (dateString: string) => {
  const date = new Date(dateString);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}`;
};

const toDateKey = (dateString: string) => dateString.slice(0, 10);

const renderContent = (content: string, linkClass = 'text-current underline') => {
  const parts = content.split(URL_REGEX);
  return parts.map((part, i) => {
    if (!/^https?:\/\//.test(part)) return <span key={i}>{part}</span>;
    const trail = part.match(TRAILING_PUNCT_REGEX)?.[0] ?? '';
    const url = trail ? part.slice(0, -trail.length) : part;
    return (
      <span key={i}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className={`break-all ${linkClass}`}
        >
          {url}
        </a>
        {trail}
      </span>
    );
  });
};

export default function AllianceChatPanel({ applicationId, agentName }: AllianceChatPanelProps) {
  const [inputValue, setInputValue] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { data, isLoading } = useFetchAllianceChatMessages(applicationId);
  const sendMessage = useSendAllianceChatMessage();
  const readMessages = useReadAllianceChatMessages();

  const messages = useMemo(() => [...(data?.records ?? [])].reverse(), [data?.records]);

  useEffect(() => {
    const hasUnread = (data?.records ?? []).some(
      (m) => m.read_at === null && m.sender_kind === 'agent'
    );
    if (hasUnread) readMessages.mutate(applicationId);
  }, [applicationId, data?.records?.length]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) container.scrollTop = container.scrollHeight;
  }, [messages.length]);

  // 入力欄は初期48px（1行）で、内容に応じて最大6行まで自動で高くなり、
  // それ以降は高さ固定でスクロールする（Figma コメント対応）
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    const maxHeight = CHAT_INPUT_LINE_HEIGHT * CHAT_INPUT_MAX_ROWS;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [inputValue]);

  const handleSend = () => {
    const content = inputValue.trim();
    if (!content && attachments.length === 0) return;
    sendMessage.mutate(
      { applicationId, content: content || undefined, attachments },
      {
        onSuccess: () => {
          setInputValue('');
          setAttachments([]);
          setAttachmentError(null);
        },
      }
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFilesSelected = (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const { accepted, error } = validateAttachments(attachments, Array.from(fileList));
    setAttachments((prev) => [...prev, ...accepted]);
    setAttachmentError(error);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  const canSend =
    (inputValue.trim().length > 0 || attachments.length > 0) && !sendMessage.isPending;

  const groupedMessages: {
    dateKey: string;
    label: string;
    messages: AgentApiAllianceChatMessagesRecord[];
  }[] = [];
  for (const message of messages) {
    const dateKey = toDateKey(message.created_at);
    const last = groupedMessages[groupedMessages.length - 1];
    if (last && last.dateKey === dateKey) {
      last.messages.push(message);
    } else {
      groupedMessages.push({
        dateKey,
        label: formatDateLabel(message.created_at),
        messages: [message],
      });
    }
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded bg-white shadow-layer1">
      <div ref={messagesContainerRef} className="flex flex-1 flex-col gap-md overflow-y-auto p-md">
        {isLoading ? null : messages.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-md">
            <div className="flex size-20 items-center justify-center rounded-full bg-[#e7e7e7]">
              <MdSend className="size-10 text-gray-500" />
            </div>
            <div className="flex flex-col items-center gap-xs">
              <p className="text-heading-xs font-bold">メッセージがありません</p>
              <p className="text-center text-body-sm text-sub">
                {agentName}さんとの
                <br />
                最初のメッセージを送信しましょう
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1" />
            {groupedMessages.map((group) => (
              <div key={group.dateKey} className="flex flex-col gap-sm">
                <div className="flex justify-center">
                  <span className="rounded-full bg-gray px-md py-xxs text-body-xs text-sub">
                    {group.label}
                  </span>
                </div>
                {group.messages.map((message) => {
                  const isOwn = message.sender_kind === 'organization_user';
                  return (
                    <div
                      key={message.id}
                      className={`flex w-full flex-col gap-xxs ${isOwn ? 'items-end pl-xxl' : 'items-start pr-xxl'}`}
                    >
                      <p className="text-body-xs text-sub">
                        {isOwn
                          ? `${message.sender_name ?? ''}（自分）`
                          : (message.sender_name ?? '')}
                      </p>
                      <div
                        className={`flex items-end gap-xs ${isOwn ? 'flex-row-reverse' : 'flex-row'}`}
                      >
                        <div
                          className={`flex flex-col gap-xs ${isOwn ? 'items-end' : 'items-start'}`}
                        >
                          {message.content && (
                            <div
                              className={`rounded-md p-sm text-body-sm break-words whitespace-pre-wrap ${
                                isOwn
                                  ? 'bg-primary text-white'
                                  : 'text-body border border-solid border-border bg-white'
                              }`}
                            >
                              {renderContent(
                                message.content,
                                isOwn ? 'text-current underline' : 'text-primary underline'
                              )}
                            </div>
                          )}
                          <ChatMessageAttachments attachments={message.attachments} isOwn={isOwn} />
                        </div>
                        <div className="flex shrink-0 flex-col items-center text-body-xs text-sub">
                          {isOwn && message.read_at && <span>既読</span>}
                          <span>{formatTime(message.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </>
        )}
      </div>

      <div className="shrink-0 border-t border-solid border-border p-md">
        <div className="flex flex-col gap-sm">
          <div className="flex flex-col gap-xs rounded-sm border border-solid border-border p-sm focus-within:border-primary">
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="メッセージを入力..."
              rows={1}
              style={{ maxHeight: `${CHAT_INPUT_LINE_HEIGHT * CHAT_INPUT_MAX_ROWS}px` }}
              className="w-full resize-none border-none bg-transparent p-0 text-body-sm leading-6 outline-none"
            />
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-xs">
                {attachments.map((file, index) => (
                  <div
                    key={`${file.name}-${index}`}
                    className="flex max-w-full items-center gap-xxs rounded-sm border border-solid border-border bg-white px-sm py-xs text-main"
                  >
                    <MdDescription className="size-5 shrink-0" />
                    <span className="min-w-0 truncate text-button-md">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      className="shrink-0 text-main hover:opacity-70"
                      aria-label={`${file.name} を削除`}
                    >
                      <MdCancel className="size-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {attachmentError && <p className="text-body-xs text-danger">{attachmentError}</p>}
          <div className="flex items-center justify-between">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ATTACHMENT_ACCEPT}
              className="hidden"
              onChange={(e) => handleFilesSelected(e.target.files)}
            />
            <Button
              type="button"
              color="secondary"
              size="md"
              iconOnly
              onClick={() => fileInputRef.current?.click()}
              aria-label="ファイルを添付"
            >
              <MdAttachFile className="size-5" />
            </Button>
            <Button
              type="button"
              color="primary"
              size="md"
              onClick={handleSend}
              disabled={!canSend}
            >
              送信
              <MdSend className="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
